import { Settings } from "@/components/Settings/types/settings";
import { zodAddressSchema } from "@/components/form-fields/AddressForm/zod/zodSchema";
import { zodComerOriginSchema } from "@/components/form-fields/ComerOriginSelect/zod/zodSchema";
import { z, ZodErrorMap, ZodIssueCode } from "zod";
import { cupsValid } from "@/components/form-fields/CUPSField";
import { FetchCUPSAvailabilityData, FetchGasCUPSAvailabilityData } from "../../../types/cups";
import { StepInstallationFormData } from "../../../types/stepsFormData";

type FormInstallationSchemaOptions = {
  settings: Settings;
  type: "electricity" | "gas";
  showChangeOwner: boolean;
  showInvoicingAddress: boolean;
  showLeadConsent: boolean;
  comerOriginEnabled: boolean;
};

const getRequiredErrorMap = (): ZodErrorMap => {
  return (issue, ctx) => {
    if (
      issue.code === ZodIssueCode.invalid_enum_value ||
      issue.code === ZodIssueCode.invalid_type
    ) {
      return { message: 'common:text.required_field' };
    }

    return { message: ctx.defaultError };
  };
};

const LeadConsentSchema = (
  settings: Settings,
  showLeadConsent: boolean,
) => {
  if (showLeadConsent && settings?.contractation?.requireLeadConsent) {
    return z.object({
      leadsConsent: z.literal(true, {
        errorMap: () => ({ message: 'common:text.required_field' }),
      }),
    });
  }

  return z.object({
    leadsConsent: z.boolean(),
  });
};

const CUPSDataSchema = (type: "electricity" | "gas") => {
  if (type === "electricity") {
    return z.object({
      accessTariff: z.number().or(z.null()),
      accessTariffName: z.string(),
      cupsAvailable: z.boolean().refine(val => val, {
        message: 'common:text.contractation_cups_not_available'
      }),
      power: z.number().nullable(),
      power2: z.number().nullable(),
      power3: z.number().optional(),
      power4: z.number().optional(),
      power5: z.number().optional(),
      power6: z.number().optional(),
      selfcons: z.boolean(),
      selfconsCode: z.string(),
      sipsDataAvailable: z.boolean(),
      address: z.object({
        zipcode: z.string(),
      })
    }) satisfies z.ZodType<FetchCUPSAvailabilityData>;
  }

  return z.object({
    accessTariff: z.number().or(z.null()),
    accessTariffName: z.string(),
    cupsAvailable: z.boolean().refine(val => val, {
      message: 'common:text.contractation_cups_not_available'
    }),
    sipsDataAvailable: z.boolean(),
    address: z.object({
      zipcode: z.string(),
    })
  }) satisfies z.ZodType<FetchGasCUPSAvailabilityData>;
};

const ResidenceSchema = (errorMap: ZodErrorMap) => {
  const CnaeSchema = z.object({
    descripcio: z.string().min(1, 'common:text.required_field'),
    id: z.number().min(1, 'common:text.required_field'),
    name: z.string().min(1, 'common:text.required_field'),
  }, {errorMap}) satisfies z.ZodType<StepInstallationFormData["cnae"]>;

  const HomeSchema = z.object({
    residenceType: z.enum(["habitual", "second"], {errorMap}),
    cnae: CnaeSchema.nullable(),
  });

  const BusinessSchema = z.object({
    residenceType: z.literal("business"),
    cnae: CnaeSchema,
  });

  return z.discriminatedUnion("residenceType", [
    HomeSchema,
    BusinessSchema,
  ]);
};

const ComerOriginSchema = (comerOriginEnabled: boolean) => {
  if (comerOriginEnabled) {
    return z.object({
      comerOrigin: zodComerOriginSchema,
    });
  }

  return z.object({
    comerOrigin: z.string(),
  });
};

const MorosityVerificationSchema = (settings: Settings) => {
  if (settings?.contractation?.mustAcceptMorosityVerification) {
    return z.object({
      acceptMorosityVerification: z.literal(true, {
        errorMap: () => ({ message: 'common:text.required_field' }),
      }),
    });
  }

  return z.object({
    acceptMorosityVerification: z.boolean(),
  });
};

const InvoiceSchema = (
  settings: Settings,
  showChangeOwner: boolean,
) => {
  const AttachmentSchema = z.object({
    name: z.string(),
    content: z.string().regex(
      /^data:.*\/[a-zA-Z]+;base64,/,
      'common:text.invalid_attachment_type',
    ),
  });
  const InvoiceAttachmentsSchema = AttachmentSchema.array();
  const ActiveContractSchema = z.object({
    activeContract: z.literal(true),
    invoice: (settings?.contractation?.invoiceRequired ?? true) ?
      InvoiceAttachmentsSchema.min(1, 'common:text.required_field') :
      InvoiceAttachmentsSchema,
    changeOwner: showChangeOwner ?
      z.boolean({required_error: 'common:text.required_field'}) :
      z.boolean().nullable().optional(),
  });
  const InactiveContractSchema = z.object({
    activeContract: z.literal(false),
    invoice: InvoiceAttachmentsSchema,
    changeOwner: z.boolean().nullable().optional(),
  });

  return z.discriminatedUnion("activeContract", [
    ActiveContractSchema,
    InactiveContractSchema,
  ]);
};

const InvoicingAddressSchema = (showInvoicingAddress: boolean) => {
  if (showInvoicingAddress) {
    return z.discriminatedUnion("sameInvoiceAddress", [
      z.object({
        sameInvoiceAddress: z.literal(true),
        ownerAddress: z.unknown().optional(),
      }),
      z.object({
        sameInvoiceAddress: z.literal(false),
        ownerAddress: zodAddressSchema,
      }),
    ]);
  }

  return z.object({
    sameInvoiceAddress: z.boolean().optional(),
    ownerAddress: z.unknown().optional(),
  });
};

const BaseInstallationSchema = (type: "electricity" | "gas") => z.object({
  cups: z.string().min(1, 'common:text.required_field')
    .superRefine((cups, ctx) => {
      if (!cupsValid(cups)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'common:text.contractation_cups_not_valid'
        });
      }
    }),

  address: zodAddressSchema,
  cupsSIPSData: CUPSDataSchema(type),
});

export const FormInstallationSchema = ({
  settings,
  type,
  showChangeOwner,
  showInvoicingAddress,
  showLeadConsent,
  comerOriginEnabled,
}: FormInstallationSchemaOptions) => {
  const requiredErrorMap = getRequiredErrorMap();

  return BaseInstallationSchema(type)
    .and(LeadConsentSchema(settings, showLeadConsent))
    .and(ResidenceSchema(requiredErrorMap))
    .and(ComerOriginSchema(comerOriginEnabled))
    .and(MorosityVerificationSchema(settings))
    .and(InvoiceSchema(settings, showChangeOwner))
    .and(InvoicingAddressSchema(showInvoicingAddress));
};
